import { useState, useEffect, useRef } from 'react'
import { Send, Loader2, Pause, Square, ChevronDown, ChevronRight, X, GitPullRequest } from 'lucide-react'
import type { Chat, ChatMessage, ChatImageBlock, SendError } from '../store/chatSlice'
import './ChatPanel.css'

const ALLOWED_IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const MAX_IMAGES = 10

interface StagedImage {
  id: string
  block: ChatImageBlock
}

function imageBlockToDataUrl(block: ChatImageBlock): string {
  return `data:${block.source.media_type};base64,${block.source.data}`
}

interface ChatPanelProps {
  chat: Chat
  sending: boolean
  sendError?: SendError | null
  interrupting?: boolean
  stopping?: boolean
  authenticated: boolean
  onSend: (content: string, images?: ChatImageBlock[]) => void
  onClearSendError?: () => void
  onInterrupt?: () => void
  onStop?: () => void
  onExpand?: () => void
}

interface MessageGroup {
  role: string
  messages: ChatMessage[]
}

function CollapsibleBlock({
  label,
  preview,
  defaultExpanded,
  children,
}: {
  label: string
  preview: string
  defaultExpanded: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultExpanded)
  return (
    <div className="chat-panel__collapsible">
      <button
        className="chat-panel__collapsible-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
        <span className="chat-panel__collapsible-label">{label}</span>
        {!open && <span className="chat-panel__collapsible-preview">{preview}</span>}
      </button>
      {open && <div className="chat-panel__collapsible-body">{children}</div>}
    </div>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function groupMessages(messages: ChatMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = []
  for (const msg of messages) {
    const last = groups[groups.length - 1]
    if (last && last.role === msg.role) {
      last.messages.push(msg)
    } else {
      groups.push({ role: msg.role, messages: [msg] })
    }
  }
  return groups
}

const URL_REGEX = /(https?:\/\/[^\s),]+[^\s),.:;])/g

function renderContentWithLinks(text: string) {
  const parts = text.split(URL_REGEX)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="chat-panel__link">{part}</a>
    ) : (
      part
    )
  )
}

function ChatPanel({ chat, sending, sendError, interrupting, stopping, authenticated, onSend, onClearSendError, onInterrupt, onStop, onExpand }: ChatPanelProps) {
  const isActive = chat.status === 'active'
  const [expanded, setExpanded] = useState(isActive)
  const [input, setInput] = useState('')
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([])
  const imageIdRef = useRef(0)

  useEffect(() => {
    if (sendError?.content) {
      setInput(sendError.content)
    }
  }, [sendError])

  const addImageFile = (file: File) => {
    if (!ALLOWED_IMAGE_MEDIA_TYPES.includes(file.type)) return
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const commaIdx = result.indexOf(',')
      if (commaIdx === -1) return
      const data = result.slice(commaIdx + 1)
      if (!data) return
      const block: ChatImageBlock = {
        type: 'image',
        source: { type: 'base64', media_type: file.type, data },
      }
      setStagedImages((prev) => {
        if (prev.length >= MAX_IMAGES) return prev
        imageIdRef.current += 1
        return [...prev, { id: `img-${imageIdRef.current}`, block }]
      })
    }
    reader.readAsDataURL(file)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return
    const imageFiles: File[] = []
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }
    if (imageFiles.length === 0) return
    // Prevent the OS from also pasting a file path / placeholder into the textarea.
    e.preventDefault()
    imageFiles.forEach(addImageFile)
  }

  const removeStagedImage = (id: string) => {
    setStagedImages((prev) => prev.filter((img) => img.id !== id))
  }

  const handleToggle = () => {
    const willExpand = !expanded
    setExpanded(willExpand)
    if (willExpand && onExpand) {
      onExpand()
    }
  }

  const handleSend = () => {
    if (sending) return
    const trimmed = input.trim()
    if (!trimmed && stagedImages.length === 0) return
    onSend(trimmed, stagedImages.length > 0 ? stagedImages.map((img) => img.block) : undefined)
    setInput('')
    setStagedImages([])
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const messages: ChatMessage[] = chat.messages || []
  const messageGroups = groupMessages(messages)
  const lastAssistantIdx = messageGroups.reduce(
    (last, g, i) => (g.role === 'assistant' ? i : last),
    -1
  )

  return (
    <div className={`chat-panel chat-panel--${chat.status}`} data-testid="chat-panel">
      <div className="chat-panel__header-row">
        <button
          className="chat-panel__header"
          onClick={handleToggle}
          aria-expanded={expanded}
          data-testid="chat-panel-header"
        >
          {expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
          <span className={`chat-panel__status chat-panel__status--${chat.status}`}>
            {isActive ? 'Active' : 'Closed'}
          </span>
          <span className="chat-panel__date">{formatDate(chat.created_at)}</span>
          {chat.container_id && (
            <span className="chat-panel__container">{chat.container_id}</span>
          )}
        </button>
        {chat.pr_url && (
          <a
            className="chat-panel__pr-link"
            href={chat.pr_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chat-pr-link"
          >
            <GitPullRequest size={12} aria-hidden="true" />
            View PR
          </a>
        )}
        {isActive && authenticated && (
          <div className="chat-panel__actions">
            {onInterrupt && (
              <button
                className="chat-panel__interrupt-btn"
                onClick={onInterrupt}
                disabled={interrupting || stopping}
                aria-label="Interrupt chat"
                data-testid="interrupt-chat-btn"
              >
                <Pause size={12} aria-hidden="true" />
                {interrupting ? 'Interrupting...' : 'Interrupt'}
              </button>
            )}
            {onStop && (
              <button
                className="chat-panel__stop-btn"
                onClick={onStop}
                disabled={stopping}
                aria-label="Stop chat"
                data-testid="stop-chat-btn"
              >
                <Square size={12} aria-hidden="true" />
                {stopping ? 'Stopping...' : 'Stop'}
              </button>
            )}
          </div>
        )}
      </div>
      {expanded && (
        <div className="chat-panel__body">
          <div className="chat-panel__messages" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="chat-panel__empty">No messages yet. Start the conversation!</div>
            )}
            {messageGroups.map((group, groupIdx) =>
              group.role === 'user' ? (
                group.messages.map((msg) => (
                  <CollapsibleBlock
                    key={msg.id}
                    label="You"
                    preview={
                      msg.content.length > 80
                        ? msg.content.slice(0, 80) + '...'
                        : msg.content || (msg.images && msg.images.length > 0 ? `${msg.images.length} image${msg.images.length > 1 ? 's' : ''}` : '')
                    }
                    defaultExpanded
                  >
                    <div
                      className="chat-panel__message chat-panel__message--user"
                      data-testid="chat-message"
                    >
                      {msg.content && (
                        <div className="chat-panel__message-content">{renderContentWithLinks(msg.content)}</div>
                      )}
                      {msg.images && msg.images.length > 0 && (
                        <div className="chat-panel__message-images" data-testid="chat-message-images">
                          {msg.images.map((img, i) => (
                            <img
                              key={i}
                              className="chat-panel__message-image"
                              src={imageBlockToDataUrl(img)}
                              alt="Attached image"
                            />
                          ))}
                        </div>
                      )}
                      <div className="chat-panel__message-time">{formatDate(msg.created_at)}</div>
                    </div>
                  </CollapsibleBlock>
                ))
              ) : (
                <CollapsibleBlock
                  key={group.messages[0].id}
                  label="Assistant"
                  preview={
                    group.messages[0].content.length > 80
                      ? group.messages[0].content.slice(0, 80) + '...'
                      : group.messages[0].content
                  }
                  defaultExpanded={groupIdx === lastAssistantIdx}
                >
                  <div
                    className="chat-panel__message-group chat-panel__message-group--assistant"
                    data-testid="chat-message-group"
                  >
                    <div className="chat-panel__message-group-entries">
                      {group.messages.map((msg) => (
                        <div key={msg.id} className="chat-panel__message-group-entry" data-testid="chat-message">
                          <span className="chat-panel__message-group-time">{formatTime(msg.created_at)}</span>
                          <span className="chat-panel__message-group-content">{renderContentWithLinks(msg.content)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CollapsibleBlock>
              )
            )}
          </div>
          {isActive && authenticated && (
            <div className="chat-panel__input-area" data-testid="chat-input-area">
              {sendError && (
                <div className="chat-panel__send-error" role="alert" data-testid="chat-send-error">
                  {sendError.message}
                </div>
              )}
              {stagedImages.length > 0 && (
                <div className="chat-panel__staged-images" data-testid="chat-staged-images">
                  {stagedImages.map((img) => (
                    <div key={img.id} className="chat-panel__staged-image" data-testid="chat-staged-image">
                      <img
                        className="chat-panel__staged-image-thumb"
                        src={imageBlockToDataUrl(img.block)}
                        alt="Pasted image preview"
                      />
                      <button
                        type="button"
                        className="chat-panel__staged-image-remove"
                        onClick={() => removeStagedImage(img.id)}
                        aria-label="Remove image"
                        data-testid="chat-staged-image-remove"
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="chat-panel__input-row">
                <textarea
                  className={`chat-panel__input${sendError ? ' chat-panel__input--error' : ''}`}
                  placeholder="Type a message or paste an image..."
                  aria-label="Chat message"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value)
                    if (sendError && onClearSendError) onClearSendError()
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  disabled={sending}
                  rows={2}
                  data-testid="chat-input"
                />
                <button
                  className="chat-panel__send-btn"
                  onClick={handleSend}
                  disabled={sending || (!input.trim() && stagedImages.length === 0)}
                  aria-label="Send message"
                  data-testid="chat-send-btn"
                >
                  {sending ? <Loader2 size={16} className="chat-panel__spinner" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ChatPanel
